import { useState, useEffect } from 'react'
import { useAuth } from '../../../contexts/AuthContext'
import { supabase } from '../../../shared/lib/supabase'
import { logAuditEvent } from '../../../shared/lib/auditLog'
import { reportClientError } from '../../../shared/lib/errorReporter'
import ForcePasswordChange from './ForcePasswordChange'
import ForceMFAEnrollment from './ForceMFAEnrollment'

function ClientOnboardingGuard({ children }) {
  const { user, profile, isClientUser, loading, updatePassword, fetchProfile, signOut } = useAuth()
  const [mfaStatus, setMfaStatus] = useState({ checked: false, enrolled: false })

  // Check MFA enrollment status (only after password is changed)
  useEffect(() => {
    if (!isClientUser || !profile || profile.must_change_password) {
      return
    }

    async function checkMFA() {
      try {
        const { data } = await supabase.auth.mfa.listFactors()
        const hasVerifiedTotp = data?.totp?.some(f => f.status === 'verified')
        setMfaStatus({ checked: true, enrolled: !!hasVerifiedTotp })
      } catch (err) {
        console.error('Error checking MFA status:', err)
        setMfaStatus({ checked: true, enrolled: false })
      }
    }

    checkMFA()
  }, [isClientUser, profile?.must_change_password])

  // Still loading auth state
  if (loading || !profile) {
    return null
  }

  // Step 1: Force password change (applies to ALL users, not just clients)
  if (profile.must_change_password) {
    return (
      <ForcePasswordChange
        onComplete={async (newPassword) => {
          logAuditEvent({ category: 'system', action: 'password.change_started', description: 'User submitted password change form' })
          try {
            const { error: pwError } = await updatePassword(newPassword)
            if (pwError) throw pwError

            const { error: profileError } = await supabase
              .from('profiles')
              .update({ must_change_password: false })
              .eq('id', user.id)
            if (profileError) throw profileError

            logAuditEvent({ category: 'system', action: 'password.changed', description: 'User completed required password change' })

            // Re-fetch profile to update state
            await fetchProfile(user.id)
          } catch (err) {
            logAuditEvent({
              category: 'system',
              action: 'password.change_failed',
              description: `Password change failed: ${err.message}`,
              metadata: { error: err.message }
            })
            // Report full error details via server endpoint (works even with broken sessions)
            reportClientError(user.id, 'password_change', err)
            throw err
          }
        }}
        onSignOut={signOut}
      />
    )
  }

  // Admin/org users skip MFA enrollment requirement (only forced for client portal users)
  if (!isClientUser) {
    return children
  }

  // Step 2: Force MFA enrollment (waiting for check)
  if (!mfaStatus.checked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#012438' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#8AD0D6' }} />
      </div>
    )
  }

  // Step 2: Force MFA enrollment
  if (!mfaStatus.enrolled) {
    return (
      <ForceMFAEnrollment
        onComplete={() => {
          setMfaStatus({ checked: true, enrolled: true })
          localStorage.setItem('ssa-mfa-verified-at', Date.now().toString())
        }}
        onSignOut={signOut}
      />
    )
  }

  // Onboarding complete — render app
  return children
}

export default ClientOnboardingGuard
